"use client";
import { useState } from "react";
import { MoreVerticalIcon, PencilIcon, TrashIcon } from "lucide-react";
import { CardLink } from "@/components/primitives/card-link";
import { CategoryBadge } from "@/components/primitives/category-badge";
import { Button } from "@/components/ui/button";
import {
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Skeleton } from "@/components/ui/skeleton";
import { formatGrams, formatKcal, formatRelativeTime } from "@/lib/formatters";
import type { IngredientUsageContext } from "@/lib/ingredients/ingredientOverview.server";
import type { Ingredient } from "@/lib/types";
import { toNumber } from "@/lib/utils";
import { trackEvent } from "@/lib/analytics";
import { DeleteIngredientDialog } from "./delete-ingredient-dialog";
import { EditIngredientDialog } from "./edit-ingredient-dialog";
export type IngredientCardModel = Ingredient & {
  usageCount?: number;
  usageContext?: IngredientUsageContext;
};
type IngredientCardProps = {
  ingredient: IngredientCardModel;
  detailsHref?: React.ComponentProps<typeof CardLink>["href"];
  editable?: boolean;
  onDelete?: () => void;
  onUpdate?: () => void;
};
function UsageLabel({
  count,
  context,
}: {
  count: number;
  context: IngredientUsageContext;
}) {
  if (count === 0 && context === "your recipes") {
    return <>Not used in your recipes yet</>;
  }
  return (
    <>
      Used in{" "}
      <span className="text-foreground font-medium">
        {count.toLocaleString()}
      </span>{" "}
      {context === "your recipes"
        ? "of your recipes"
        : count === 1
          ? "public recipe"
          : "public recipes"}
    </>
  );
}
function IngredientMetric({ label, value }: { label: string; value: string }) {
  return (
    <div
      className="flex min-w-0 flex-col gap-1 text-xs leading-none"
      aria-label={`${label}: ${value}`}
    >
      <dt className="text-muted-foreground truncate text-center font-medium">
        {label}
      </dt>
      <dd className="truncate text-center leading-tight font-medium">
        {value}
      </dd>
    </div>
  );
}
function formatCompositionGrams(value: number | string) {
  return formatGrams(toNumber(value) * 100, 0, 1);
}
export function IngredientCardSkeleton() {
  return (
    <div className="card rounded-xl" aria-hidden>
      <div className="space-y-4 p-4 md:p-6">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1 space-y-2">
            <Skeleton className="h-5 w-36 max-w-full" />
            <div className="flex gap-1">
              <Skeleton className="h-5 w-20" />
              <Skeleton className="h-5 w-16" />
            </div>
          </div>
          <Skeleton className="size-8 rounded-md" />
        </div>
        <Skeleton className="h-4 w-32" />
      </div>
      <CardFooter className="relative grid grid-cols-4 gap-1 px-1 py-3 before:absolute before:inset-x-6">
        {Array.from({ length: 4 }).map((_, index) => (
          <div key={index} className="flex min-w-0 flex-col gap-1 px-2 py-1">
            <Skeleton className="mx-auto h-3 w-10 max-w-full" />
            <Skeleton className="mx-auto h-4 w-10 max-w-full" />
          </div>
        ))}
      </CardFooter>
    </div>
  );
}
export function IngredientCard({
  ingredient,
  detailsHref,
  editable = false,
  onDelete,
  onUpdate,
}: IngredientCardProps) {
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const handleUpdate = () => {
    setShowEditDialog(false);
    onUpdate?.();
  };
  return (
    <>
      <CardLink
        href={detailsHref ?? `/ingredients/${ingredient.slug}`}
        className="grid grid-rows-[1fr_auto]"
        srLabel={`View ${ingredient.name} details`}
      >
        <CardHeader className="h-full">
          <div className="min-w-0 flex-1">
            <div className="flex min-w-0 items-start justify-between">
              <CategoryBadge category={ingredient.category} size="sm" />
              {editable ? (
                <DropdownMenu>
                  <DropdownMenuTrigger
                    render={
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        aria-label={`Actions for ${ingredient.name}`}
                        className="relative z-20 -mt-2 -mr-2 size-9 shrink-0"
                      />
                    }
                  >
                    <MoreVerticalIcon aria-hidden />
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => setShowEditDialog(true)}>
                      <PencilIcon aria-hidden /> Edit
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={() => setShowDeleteDialog(true)}
                      variant="destructive"
                    >
                      <TrashIcon aria-hidden /> Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <div className="size-7" />
              )}
            </div>
            <CardTitle className="line-clamp-2 group-hover:underline">
              {ingredient.name}
            </CardTitle>
          </div>

          {ingredient.usageCount !== undefined && ingredient.usageContext ? (
            <CardDescription>
              <UsageLabel
                count={ingredient.usageCount}
                context={ingredient.usageContext}
              />
            </CardDescription>
          ) : editable ? (
            <CardDescription className="text-muted-foreground">
              Created {formatRelativeTime(ingredient.createdAt)}
            </CardDescription>
          ) : null}
        </CardHeader>

        <CardFooter className="grid grid-cols-4 gap-1 border-t px-1 py-3">
          <dl className="contents">
            <IngredientMetric
              label="Water"
              value={formatCompositionGrams(ingredient.waterPct)}
            />
            <IngredientMetric
              label="Fat"
              value={formatCompositionGrams(ingredient.fatPct)}
            />
            <IngredientMetric
              label="Sugar"
              value={formatCompositionGrams(ingredient.sugarsPct)}
            />
            <IngredientMetric
              label="Calories"
              value={formatKcal(toNumber(ingredient.energyKcal) * 100)}
            />
          </dl>
        </CardFooter>
      </CardLink>

      {editable ? (
        <>
          <DeleteIngredientDialog
            ingredient={ingredient}
            open={showDeleteDialog}
            onOpenChange={setShowDeleteDialog}
            onDeleted={() => {
              onDelete?.();
              trackEvent("user-ingredient-deleted");
            }}
          />

          <EditIngredientDialog
            ingredient={ingredient}
            open={showEditDialog}
            onOpenChange={setShowEditDialog}
            onSuccess={handleUpdate}
          />
        </>
      ) : null}
    </>
  );
}
